import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { templateService, templatePreviewUrl, type Template } from '../../services/template.service';
import { useApplyTemplate } from '../landing/useApplyTemplate';
import { BootOverlay, clearBoot, startBoot, type BootCopy } from '../landing/BootOverlay';
import { useLandingTheme } from '../landing/useLandingTheme';
import { FONT_SANS, FONT_SERIF } from '../landing/landingData';
import { ArrowR, ComingSoon, FillButton, ICON, Icon, PageHead, useDash } from './dashUi';

/**
 * Dashboard Templates (design "Templates"): Websites / Videos switch, category
 * chips and template cards. Websites come from the real catalog and "Use this"
 * applies one (new project + template files, then the studio). There is no video
 * template data yet, so the Videos tab is a coming-soon card.
 */

type Tab = 'sites' | 'videos';

const seg = (on: boolean): React.CSSProperties => ({ height: 30, padding: '0 14px', border: 0, borderRadius: 999, background: on ? 'var(--hv-card)' : 'transparent', color: on ? 'var(--hv-ink)' : 'var(--hv-muted)', boxShadow: on ? '0 2px 5px -2px var(--hv-shadow)' : 'none', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' });
const chip = (on: boolean): React.CSSProperties => ({ height: 32, padding: '0 13px', borderRadius: 999, border: `1px solid ${on ? 'var(--hv-ink)' : 'var(--hv-line)'}`, background: on ? 'var(--hv-ink)' : 'var(--hv-card)', color: on ? 'var(--hv-page)' : 'var(--hv-body)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' });

// Design hovers (data-hover) that have no shared class yet.
const LOCAL_CSS = `
.hv-dash .hv-tplcard { transition: transform 0.35s cubic-bezier(0.2,0.9,0.24,1), box-shadow 0.35s ease; }
.hv-dash .hv-tplcard:hover { transform: translateY(-4px); box-shadow: 0 40px 60px -40px var(--hv-shadow) !important; }
.hv-dash .hv-tpluse:hover:not(:disabled) { background: var(--hv-mint) !important; color: var(--hv-mint-ink) !important; border-color: var(--hv-mint) !important; }
.hv-dash .hv-tplchip:hover { border-color: var(--hv-brand-ink) !important; }
`;

const TemplateCard: React.FC<{ t: Template; busy: boolean; disabled: boolean; onUse: () => void }> = ({ t, busy, disabled, onUse }) => {
    const meta = [`Website · ${t.category}`, t.usageCount > 0 ? `${t.usageCount} ${t.usageCount === 1 ? 'app' : 'apps'} created` : ''].filter(Boolean).join(' · ').toUpperCase();
    return (
        <div className="hv-tplcard" style={{ display: 'flex', flexDirection: 'column', borderRadius: 8, overflow: 'hidden', background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 30px 50px -42px var(--hv-shadow)' }}>
            <div style={{ position: 'relative', height: 180, background: 'var(--hv-page-2)' }}>
                {t.hasPreview ? (
                    <div role="img" aria-label={`${t.name} preview`} style={{ position: 'absolute', inset: 0, backgroundImage: `url("${templatePreviewUrl(t.id)}")`, backgroundSize: 'cover', backgroundPosition: 'top' }} />
                ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', padding: 16, background: 'linear-gradient(150deg,var(--hv-mint),var(--hv-page-2))', fontFamily: FONT_SERIF, fontSize: 24, lineHeight: 1.05, color: 'var(--hv-mint-ink)', opacity: 0.75 }}>{t.name}</div>
                )}
                {t.badge && (
                    <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 6, height: 26, padding: '0 11px', borderRadius: 999, background: 'var(--hv-card)', fontFamily: FONT_SANS, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--hv-mint-ink)', pointerEvents: 'none', boxShadow: '0 8px 16px -12px var(--hv-shadow)', textTransform: 'uppercase' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2 8 8 2-8 2-2 8-2-8-8-2 8-2z" /></svg>
                        {t.badge}
                    </div>
                )}
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em', color: 'var(--hv-ink)' }}>{t.name}</h3>
                <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.6, color: 'var(--hv-body)', textWrap: 'pretty' } as React.CSSProperties}>{t.description}</p>
                <div style={{ flex: 1, minHeight: 16 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 14, borderTop: '1px dashed var(--hv-line-2)' }}>
                    <span title={meta} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: FONT_SANS, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>{meta}</span>
                    <div style={{ flex: 1 }} />
                    <button type="button" className="hv-tpluse" data-track="dash_template_use" data-track-label={t.id} disabled={disabled} onClick={onUse} style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 7, height: 34, padding: '0 14px', borderRadius: 999, border: '1px solid var(--hv-line-2)', color: 'var(--hv-ink)', fontSize: 12.5, fontWeight: 700, cursor: disabled ? 'default' : 'pointer', background: 'transparent', opacity: disabled && !busy ? 0.6 : 1 }}>
                        {busy ? 'Opening…' : 'Use this'}
                        <ArrowR />
                    </button>
                </div>
            </div>
        </div>
    );
};

export const DashTemplates: React.FC = () => {
    const dash = useDash();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [theme] = useLandingTheme();
    const [tab, setTab] = useState<Tab>('sites');
    const [cat, setCat] = useState('All');
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [boot, setBoot] = useState<BootCopy | null>(null);

    const { applying, apply } = useApplyTemplate({
        isAuthenticated,
        launch: (copy) => { setBoot(copy); startBoot(copy); },
        abortLaunch: () => { setBoot(null); clearBoot(); },
        onCreatedEmpty: () => {
            dash.notify('error', 'The project was created but the template could not be applied. You can find it in Projects.');
            void dash.refreshProjects();
        },
    });

    const load = () => {
        setLoading(true);
        void templateService.list().then((r) => {
            if (r.success && r.data) { setTemplates(r.data); setError(null); }
            else setError((r as { message?: string }).message || 'Could not load templates.');
            setLoading(false);
        });
    };
    useEffect(load, []);

    const cats = useMemo(() => ['All', ...Array.from(new Set(templates.map((t) => t.category).filter(Boolean)))], [templates]);
    const curCat = cats.includes(cat) ? cat : 'All';
    const shown = curCat === 'All' ? templates : templates.filter((t) => t.category === curCat);

    return (
        <section data-screen-label="Templates" style={{ display: 'flex', flexDirection: 'column', gap: 32, animation: 'hvRise 0.4s cubic-bezier(0.22,0.9,0.24,1) both' }}>
            <style>{LOCAL_CSS}</style>
            <PageHead title="Templates" lead="Pick one and we fill it in with your details." />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div role="tablist" aria-label="Template type" style={{ display: 'flex', gap: 3, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                    <button type="button" role="tab" aria-selected={tab === 'sites'} onClick={() => { setTab('sites'); setCat('All'); }} style={seg(tab === 'sites')}>Websites</button>
                    <button type="button" role="tab" aria-selected={tab === 'videos'} onClick={() => { setTab('videos'); setCat('All'); }} style={seg(tab === 'videos')}>Videos</button>
                </div>
                {tab === 'sites' && cats.length > 1 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {cats.map((c) => (
                            <button key={c} type="button" className="hv-tplchip" aria-pressed={c === curCat} onClick={() => setCat(c)} style={chip(c === curCat)}>{c}</button>
                        ))}
                    </div>
                )}
            </div>

            {tab === 'videos' ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                    <div style={{ alignSelf: 'stretch' }}>
                        <ComingSoon title="Video templates are coming soon" text="Until then, describe the video you want on Home and HyVay writes the script, scenes and captions for you." />
                    </div>
                    <FillButton onClick={() => navigate('/dashboard')} icon={<Icon d={ICON.video} size={15} />}>Make a video</FillButton>
                </div>
            ) : loading ? (
                <div style={{ padding: '24px 0', fontFamily: FONT_SANS, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>LOADING…</div>
            ) : error ? (
                <div style={{ padding: '64px 24px', textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_SERIF, fontSize: 26, letterSpacing: '0.01em', wordSpacing: '0.06em' }}>Could not load templates</div>
                    <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hv-body)' }}>{error}</div>
                    <button type="button" className="hv-btn-out" onClick={load} style={{ margin: '16px auto 0', height: 34, padding: '0 14px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Try again</button>
                </div>
            ) : shown.length === 0 ? (
                <div style={{ padding: '64px 24px', textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_SERIF, fontSize: 26, letterSpacing: '0.01em', wordSpacing: '0.06em' }}>No templates yet</div>
                    <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hv-body)' }}>Describe your site on Home and HyVay builds it from scratch.</div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 16 }}>
                    {shown.map((t) => (
                        <TemplateCard key={t.id} t={t} busy={applying === t.id} disabled={!!applying} onUse={() => void apply(t)} />
                    ))}
                </div>
            )}

            {boot && <BootOverlay copy={boot} dark={theme === 'dark'} />}
        </section>
    );
};

export default DashTemplates;
